<template>
    <DefaultTemplate :model="model">
        <div :class="[`bg-${model.color}-500`]" class="bg-opacity-90">
            <div class="container-narrow">
                <div class="category-header flex flex-col md:flex-row items-start md:items-end justify-between">
                    <div>
                        <Breadcrumbs :breadcrumbs="model.breadcrumbs" className="text-white" skipLast />
                        <h1 v-epi-edit="'Name'" class="text-white my-3">{{ model.name }}</h1>
                    </div>
                    <CategoryNavigation :categories="categories" :activeCategory="this.model.contentLink.guidValue" />
                </div>
            </div>
        </div>
        <!-- Content -->
        <div class="container-narrow">
            <div class="mb-12 mt-4 grid grid-cols-3 gap-4">
                <div class="col-span-3 sm:col-span-2 pt-7" :key="`items_count_${items.length}`">
                    <!-- Mobile filter -->
                    <div class="sm:hidden flex flex-col justify-end items-end mb-4">
                        <button class="uppercase mb-2" @click="toggleMobileFilterOpen">Filtrer <font-awesome-icon icon="filter" /></button>
                        <div class="w-full ease-in duration-200 delay-75 overflow-hidden"
                             :class="[filterOpen ? 'max-h-screen transition-maxHeight' : 'max-h-0']">
                            <div class="bg-white mb-4">
                                <CategoryFilter v-if="!loading"
                                                :key="`filters_${this.allTargets.length}`"
                                                :allTargets="this.allTargets"
                                                :selectedFilters="this.selectedFilters"
                                                @handleCheckbox="handleCheckbox"
                                                @resetFilters="resetFilters" />
                            </div>

                            <div class="mb-6">
                                <p class="font-bold mb-1 px-2">Søk i {{ model.name }}</p>
                                <div class="flex flex-col">
                                    <input v-model="searchString"
                                           class="w-full p-3"
                                           placeholder="Søk på tittel, forfatter o.l." />
                                    <div class="flex items-center justify-end mt-4">
                                        <button v-if="searchString" class="mr-4" @click="resetSearch">Nullstill <font-awesome-icon class="ml-1" icon="times" /></button>
                                        <button class="btn btn-green btn-xs" @click="search">SØK</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="loading">
                        <LoadingSpinner size="2x" />
                    </div>
                    <div v-else>
                        <div v-if="searchStringText" class="mb-1">
                            Fant <strong> {{ items.length }} </strong> treff ved søk på "{{ searchStringText }}" i {{ model.name }}.
                        </div>

                        <a v-for="item in items"
                           :key="item.id"
                           :href="item.url || '/'"
                           :class="['flex flex-row w-full mb-4 bg-white border-t-6 hover:shadow', `border-${item.color}-500`]">
                            <div class="grid grid-cols-3 w-full">
                                <img :src="`${item.image}?${getImageParams(item)}`" class="inline-block" />
                                <div class="col-span-2 pt-3 pr-2 pb-3 pl-5 lg:pl-0">
                                    <h3>{{ item.title }}</h3>
                                    <p class="py-2 text-sm text-grey-300">({{ new Date(item.publishDate).toLocaleDateString('nb-NO', dateOptions) }})</p>
                                    <p class="text-sm" :inner-html.prop="item.extract | truncate(150)" />
                                </div>
                            </div>
                        </a>
                    </div>
                </div>

                <div class="hidden sm:inline-block" v-if="!loading">
                    <div v-show="!model.hideContentFilter" class="mb-6">
                        <p class="font-bold mb-1">Filtrer innhold</p>
                        <div class="bg-white">
                            <CategoryFilter v-if="!loading"
                                            :key="`filters_${this.allTargets.length}`"
                                            :allTargets="this.allTargets"
                                            :selectedFilters="this.selectedFilters"
                                            @handleCheckbox="handleCheckbox"
                                            @resetFilters="resetFilters" />
                        </div>
                    </div>
                    <div v-show="!model.hideContentSearch" class="mb-6">
                        <p class="font-bold mb-1">Søk i {{ model.name }}</p>
                        <div class="flex flex-col">
                            <input v-model="searchString"
                                   class="w-full p-3"
                                   placeholder="Søk på tittel, forfatter o.l." />
                            <div class="flex items-center justify-end mt-4">
                                <button v-if="searchString" class="mr-4" @click.prevent="resetSearch">Nullstill <font-awesome-icon class="ml-1" icon="times" /></button>
                                <button class="btn btn-green btn-xs" @click.prevent="search">SØK</button>
                            </div>
                        </div>
                    </div>
                    <div v-show="inEditMode">
                        <EpiProperty v-if="model.ShowCategoryNavigation" propertyName="ShowCategoryNavigation" displayText="Vis kategorinavigasjon" />
                        <EpiProperty propertyName="HideContentFilter" displayText="Skjul innholdsfilter" />
                        <EpiProperty propertyName="HideContentSearch" displayText="Skjul innholdssøk" />
                        <EpiProperty propertyName="Color" displayText="Kategorifarge" />
                        <EpiProperty propertyName="BildeValgt" displayText="Bilde valgt kategori" />
                        <EpiProperty propertyName="BildeIkkeValgt" displayText="Bilde ikke valgt kategori" />
                    </div>
                </div>
            </div>
        </div>
    </DefaultTemplate>
</template>

<script>
import Breadcrumbs from '@/Scripts/components/organisms/Breadcrumbs/breadcrumbs.vue';
import CategoryNavigation from '@/Scripts/components/organisms/CategoryNavigation/categorypage.vue';
import EpiLink from '@/Scripts/components/atoms/EpiLink/index.vue';
import CategoryFilter from '@/Scripts/components/organisms/Filters/categoryFilter.vue';
import LoadingSpinner from '@/Scripts/components/atoms/LoadingSpinner/loadingSpinner.vue';
import api from '@/Scripts/api/api';
import { mapState } from 'vuex';
import EpiProperty from '@/Scripts/components/EpiProperty.vue';

export default {
    props: ['model'],
    components: {
        Breadcrumbs,
        CategoryNavigation,
        EpiLink,
        CategoryFilter,
        LoadingSpinner,
        EpiProperty,
    },
    computed: mapState({
        inEditMode: state => state.epiContext.inEditMode
    }),
    data() {
        return {
            categoryId: this.model.contentLink.id,
            loading: true,
            categories: (this.model.categoryNavigation && this.model.categoryNavigation.kategorier
                ? this.model.categoryNavigation.kategorier
                : []
            ),
            items: [],
            dateOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
            imageParams: 'scale=both&width=180&height=112',
            allTargets: this.model.targets,
            selectedFilters: [],
            searchString: '',
            filterOpen: false,
            searchStringText: null
        };
    },
    mounted() {
        this.search();
    },
    methods: {
        log(obj) {
            console.log(obj);
        },
        getImageParams(item) {
            let params = 'scale=both&width=180&height=180';
            return (item.imageResizerParams ? `${params}&${item.imageResizerParams}` : params);
        },
        toggleMobileFilterOpen() {
            this.filterOpen = !this.filterOpen;
        },
        resetFilters() {
            this.selectedFilters = [];
            this.search();
        },
        resetSearch() {
            this.searchString = '';
            this.searchStringText = null;
            this.search();
        },
        updateTargetCounts(targets) {
            return this.allTargets.map((t) => {
                return {
                    ...t,
                    count: targets[t.id] || 0
                };
            });
        },
        handleCheckbox(filters) {
            if (filters && filters.length > 0) {
                this.selectedFilters = filters;
            } else {
                this.selectedFilters = [];
            }
            this.search();
        },
        search() {
            // Perform search
            this.loading = true;
            this.searchStringText = null;
            const parameters = {
                parentid: this.categoryId,
                targets: (this.selectedFilters && this.selectedFilters.length > 0 ? this.selectedFilters.join(',') : ''),
                words: this.searchString
            };
            api.categorySearch(parameters).then(response => {
                if (response.status === 200) {
                    this.setState({
                        items: response.data.Hits,
                        allTargets: this.updateTargetCounts(response.data.Facets),
                        filterOpen: false,
                        loading: false,
                        searchStringText: this.searchString
                    });
                }
            });
        },
        setState(obj) {
            Object.assign(this, obj);
        }
    }
};
</script>
